import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import { PDFDocument } from "pdf-lib";
import { toast } from "react-toastify";
import { usePdf } from "../contexts/PdfContext";
import { useUser } from "../contexts/UserContext";
import Loader from "../utils/Loader";
import { useGoogleApi } from "../contexts/GoogleApiContext";
import BottomControls from "../components/Pdf/BottomControls";
import FieldManagerPanel from "../components/Pdf/FieldManager";
import TextEditorToolbar from "../components/Pdf/TextEditorToolbar";
import AutoResizeTextBox from "../components/Pdf/AutoResizeTextBox";
import AutoCheckbox from "../components/Pdf/AutoCheckbox";
import AutoImage from "../components/Pdf/AutoImage";
import { generateFilledPdf } from "../utils/Utility";
import PdfToolbar from "../components/Pdf/PdfToolbar";
import CheckboxEditorToolbar from "../components/Pdf/CheckboxEditorToolbar";
import AutoSignature from "../components/Pdf/AutoSignature";
import { uploadCloudApi, deleteCloudApi } from "../utils/cloudApi";

const GRID_SIZE = 5;
const SCROLL_EDGE = 100;
const SCROLL_SPEED = 2;
// Set the PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs";

const PdfWithSidebar = () => {
  // ---------------------------
  // ROUTER & CONTEXT HOOKS
  // ---------------------------
  const { id } = useParams();
  const navigate = useNavigate();
  const { savePdf, pdfs, sigUrl, saveSignature, setSigUrl } = usePdf();
  const { fetchPdfFile } = useGoogleApi();
  const { user, userData } = useUser();

  // ---------------------------
  // STATE & REFS
  // ---------------------------
  const [pdf, setPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const fieldsRef = useRef(fields);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [pdfPages, setPdfPages] = useState([]);
  const [selectedColor] = useState("#000000");
  const [selectedFillColor] = useState("transparent");
  const [selectedTextAlign] = useState("left");
  const [selectedFontSize, setSelectedFontSize] = useState(12);
  const [selectedFontFamily, setSelectedFontFamily] = useState("Arial");
  const [selectedCheckbox, setSelectedCheckbox] = useState("check");

  const textboxCountersRef = useRef({});
  const checkboxCountersRef = useRef({});
  const [fitMode, setFitMode] = useState("width");
  const [fitWidthZoom, setFitWidthZoom] = useState(0);

  // Refs for PDF rendering and interactions
  const [pdfReady, setPdfReady] = useState(false);
  const pdfRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  const baseSizeRef = useRef({ w: 0, h: 0, ptPerPx: 1 });
  const renderTaskRef = useRef(null);
  const dragIndexRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const resizeFieldRef = useRef(null);
  const resizeStartXRef = useRef(0);
  const resizeStartYRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const resizeStartHeightRef = useRef(0);
  const resizeStartTopRef = useRef(0);
  const resizeSideRef = useRef("right");
  const resizeStartLeftRef = useRef(0);
  const containerRef = useRef(null);
  const frameRef = useRef(null);

  const [activeTool, setActiveTool] = useState(null);
  const isTextboxMode = activeTool === "text";
  const isCheckboxMode = activeTool === "checkbox";
  const isPanMode = activeTool === "pan";
  // const showSigner = activeTool === "signature";
  const [showSigner, setShowSigner] = useState(false);
  // Convenience: Extract Google Drive ID from current PDF.
  const googleDriveId = pdf?.fileId;
  const deletedImageUrlsRef = useRef([]);
  const pendingUploadsRef = useRef([]);
  const [saveLoading, setSaveLoading] = useState(false);

  // initialize it to null
  const [pdfBytes, setPdfBytes] = useState(null);
  const [showLeftSidebar, setShowLeftSidebar] = useState(false);
  const [showRightSidebar, setShowRightSidebar] = useState(false);

  // const isEmbedded = window.self !== window.top;
  const isDraggingRef = useRef(false);
  const downXRef = useRef(0);
  const downYRef = useRef(0);

  const [isEditingText, setIsEditingText] = useState(false);

  const selectField = useCallback((id) => {
    setSelectedFieldId(id);
  }, []);

  useEffect(() => {
    const selectedField = fields.find((f) => f.id === selectedFieldId);
    if (selectedField?.type === "checkbox" && selectedField.checkboxStyle) {
      setSelectedCheckbox(selectedField.checkboxStyle);
    }
  }, [selectedFieldId, fields]);

  useEffect(() => {
    if (!sigUrl) return;
    setFields((all) =>
      all.map((f) => (f.type === "signature" ? { ...f, src: sigUrl } : f))
    );
  }, [sigUrl]);

  // Scroll the container to center the selected field whenever it changes
  const scrollFieldIntoView = useCallback(
    (fieldId) => {
      if (fieldId == null) return;
      const field = fields.find((f) => f.id === fieldId);
      if (!field) return;

      // if it lives on a different page, switch to that page first
      if (field.page !== currentPage) {
        setCurrentPage(field.page);
        return;
      }
      const container = containerRef.current;
      if (!container) return;

      const widthPx = (field.width ?? field.baseWidth) * zoom;
      const heightPx = (field.height ?? field.baseHeight) * zoom;
      const leftPx = field.baseLeft * zoom;
      const topPx = field.baseTop * zoom;

      container.scrollLeft = leftPx - container.clientWidth / 2 + widthPx / 2;
      container.scrollTop = topPx - container.clientHeight / 2 + heightPx / 2;
    },
    [fields, zoom, currentPage, setCurrentPage]
  );

  const handleThumbnailClick = (pageNum) => {
    setActiveTool(null);
    setSelectedFieldId(null);
    setZoom(1.0);
    if (pdfCanvasRef.current) pdfCanvasRef.current.style.transform = "scale(1)";
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
    setCurrentPage(pageNum);
    renderPage(pageNum);
  };

  useLayoutEffect(() => {
    if (selectedFieldId == null) return;
    scrollFieldIntoView(selectedFieldId);
  }, [currentPage, selectedFieldId, zoom, scrollFieldIntoView]);

  const renderPdf = useCallback(async () => {
    try {
      const original = await fetchPdfFile(googleDriveId);
      setPdfBytes(original);

      const pdfjsBytes = original.slice(0);
      const pdfLibBytes = original.slice(0);

      const loadingTask = pdfjsLib.getDocument({ data: pdfjsBytes });
      const pdfDocument = await loadingTask.promise;
      pdfRef.current = pdfDocument;
      setTotalPages(pdfDocument.numPages);

      await generateThumbnails(pdfDocument);
      await PDFDocument.load(pdfLibBytes);

      setPdfLoading(false);
      setPdfReady(true);
    } catch (error) {
      console.error("Error rendering PDF:", error);
      toast.error("Error rendering PDF.");
      setPdfLoading(false);
    }
  }, [fetchPdfFile, googleDriveId]);

  const nextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const previousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleSignatureSave = (fieldId, dataUrl) => {
    setShowSigner(false);
    setActiveTool(null);

    // 1) Collect old signature URLs for cleanup
    const oldSigUrl = sigUrl;

    // queue them all for deletion
    deletedImageUrlsRef.current.push(oldSigUrl);

    // 2) Immediately replace every signature field in state
    setSigUrl(dataUrl);

    // 3) Upsert exactly one pending upload for signatures
    const sigQueue = pendingUploadsRef.current;
    const existingIdx = sigQueue.findIndex((u) => u.fieldType === "signature");
    if (existingIdx > -1) {
      sigQueue[existingIdx].dataUrl = dataUrl;
    } else {
      sigQueue.push({ fieldType: "signature", dataUrl });
    }

    // 4) If there was no fieldId (brand new signature), create one
    if (!fieldId) {
      handleImageUpload(dataUrl, {
        namePrefix: "Signature",
        fieldType: "signature",
      });
    }

    // no more “selected” now that we’ve committed it
    setSelectedFieldId(null);
  };

  const onSignatureClick = () => {
    if (sigUrl) {
      // drop the existing signature onto the current page
      handleImageUpload(sigUrl, {
        namePrefix: "Signature",
        fieldType: "signature",
      });
    } else {
      // open the drawing pad
      setActiveTool("signature");
      setShowSigner(true);
    }
  };

  const handleDoubleClick = (field) => {
    setSelectedFieldId(field.id);
    setActiveTool("signature");
    setShowSigner(true);
  };

  // ---------------------------
  // FIELD (RIBBON) FUNCTIONS
  // ---------------------------
  const handleCanvasClick = (e) => {
    if (e.target === pdfCanvasRef.current || e.target === frameRef.current) {
      setSelectedFieldId(null);
    }
    if (!pdfCanvasRef.current || !frameRef.current) return;

    const rect = pdfCanvasRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const cssX = clickX / zoom;
    const cssY = clickY / zoom;
    const baseLeft = cssX;
    const baseTop = cssY;

    if (isTextboxMode) {
      if (!textboxCountersRef.current[currentPage]) {
        textboxCountersRef.current[currentPage] = 1;
      } else {
        textboxCountersRef.current[currentPage] += 1;
      }

      const number = textboxCountersRef.current[currentPage];
      const baseHeight = 40;
      const baseWidth = 30;

      const newTextbox = {
        id: Date.now(),
        type: "textbox",
        name: `New Text ${number}`,
        number: `${number}`,
        text: "",
        baseWidth: baseWidth,
        baseHeight: baseHeight,
        width: baseWidth,
        height: baseHeight,
        baseTop,
        baseLeft,
        textAlign: selectedTextAlign,
        color: selectedColor,
        fontFamily: selectedFontFamily,
        fillColor: selectedFillColor,
        size: selectedFontSize,
        page: currentPage,
      };

      setFields((prev) => [...prev, newTextbox]);
      setIsEditingText(true);
      selectField(newTextbox.id);
      setActiveTool(null);
    }

    if (isCheckboxMode) {
      const allCheckboxes = fields.filter((f) => f.type === "checkbox");

      // pick the last one (if any)
      const lastCheckbox =
        allCheckboxes.length > 0
          ? allCheckboxes[allCheckboxes.length - 1]
          : null;

      // derive its size (fall back to 30px if none exist)
      const checkboxSize = lastCheckbox
        ? lastCheckbox.width ?? lastCheckbox.baseWidth
        : 30;
      if (!checkboxCountersRef.current[currentPage]) {
        checkboxCountersRef.current[currentPage] = 1;
      } else {
        checkboxCountersRef.current[currentPage] += 1;
      }

      const number = checkboxCountersRef.current[currentPage];

      const newCheckbox = {
        id: Date.now(),
        type: "checkbox",
        name: `Checkbox ${number}`,
        number: `${number}`,
        checked: true,
        checkboxStyle: selectedCheckbox,
        baseTop: baseTop - checkboxSize / 2,
        baseLeft: baseLeft - checkboxSize / 2,
        baseWidth: checkboxSize,
        baseHeight: checkboxSize,
        page: currentPage,
      };

      setFields((prev) => [...prev, newCheckbox]);
      selectField(newCheckbox.id);
    }
  };

  // ---------------------------
  // Image Functions
  // ---------------------------
  function handleImageUpload(
    dataUrl,
    { namePrefix = "Image", fieldType = "image" } = {}
  ) {
    const img = new Image();
    img.src = dataUrl;
    img.onload = () => {
      const maxDim = 150;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const scale = Math.min(1, maxDim / Math.max(w, h));
      const baseWidth = w * scale;
      const baseHeight = h * scale;

      const existingCount = fields.filter(
        (f) => f.page === currentPage && f.type === fieldType
      ).length;
      const newField = {
        id: Date.now(),
        name: `${namePrefix} ${existingCount + 1}`,
        type: fieldType,
        src: dataUrl,
        baseLeft: 100,
        baseTop: 100,
        baseWidth,
        baseHeight,
        page: currentPage,
      };

      setFields((f) => [...f, newField]);
      pendingUploadsRef.current.push({
        fieldId: newField.id,
        dataUrl,
        fieldType: fieldType,
      });
      selectField(newField.id);
    };
    img.onerror = () => toast.error("Failed to load image for sizing");
  }

  const removeField = (id) => {
    setFields((prev) => {
      const fieldToRemove = prev.find((f) => f.id === id);
      if (fieldToRemove?.type === "image" && fieldToRemove.src) {
        deletedImageUrlsRef.current.push(fieldToRemove.src);
        // console.log(fieldToRemove.src);
      }
      return prev.filter((field) => field.id !== id);
    });
  };

  const updateFieldName = (fieldId, newName) =>
    setFields((prev) =>
      prev.map((field) =>
        field.id === fieldId ? { ...field, name: newName } : field
      )
    );

  // This function merges any updated properties into the specified field.
  const updateField = useCallback((fieldId, updates) => {
    setFields((prevFields) =>
      prevFields.map((field) =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    );
  }, []);

  const saveFieldPositions = useCallback(async () => {
    const current = fieldsRef.current;

    if (!current.length) {
      toast.error("No fields to save.");
      return;
    }

    // console.log(current);

    // try {
    //   setSaveLoading(true);

    //   // 1) partition pendingUploads
    //   const sigPending = [];
    //   const otherPending = [];
    //   pendingUploadsRef.current.forEach((item) => {
    //     if (item.fieldType === "signature") sigPending.push(item);
    //     else otherPending.push(item);
    //   });

    //   // start from a fresh copy
    //   let updated = [...current];

    //   // 2) upload *one* signature if needed
    //   if (sigPending.length > 0) {
    //     const { dataUrl } = sigPending[0];

    //     let signatureUrl = dataUrl;
    //     if (!dataUrl.startsWith("https://")) {
    //       signatureUrl = await uploadCloudApi(dataUrl, user.uid, "img", "sig");
    //       await saveSignature(signatureUrl, user.uid);
    //     }
    //   }

    //   // 3) upload other images
    //   await Promise.all(
    //     otherPending.map(async ({ fieldId, dataUrl }) => {
    //       const publicUrl = dataUrl.startsWith("https://")
    //         ? dataUrl
    //         : await uploadCloudApi(dataUrl, user.uid, "img", "pdfImg");
    //       updated = updated.map((f) =>
    //         f.id === fieldId ? { ...f, src: publicUrl } : f
    //       );
    //     })
    //   );

    //   // 4) commit to state & clear queue
    //   setFields(updated);
    //   pendingUploadsRef.current = [];

    //   // 5) group & save
    //   const grouped = updated.reduce((acc, f) => {
    //     (acc[f.page] = acc[f.page] || []).push(f);
    //     return acc;
    //   }, {});
    //   await savePdf(pdf.id, { ...pdf, fields: grouped }, user.uid);
    //   toast.success("File saved successfully!");

    //   // 6) clean up deletes (extract only the URLs!)
    //   const urls = deletedImageUrlsRef.current;
    //   deletedImageUrlsRef.current = [];
    //   await Promise.all(
    //     urls.map((url) =>
    //       deleteCloudApi(url, user.uid).catch((err) =>
    //         console.warn("Failed to delete image", url, err)
    //       )
    //     )
    //   );
    // } catch (error) {
    //   console.error(error);
    //   toast.error("Error saving PDF");
    // } finally {
    //   setSaveLoading(false);
    // }
  }, [pdf, savePdf, user.uid, saveSignature, deleteCloudApi]);

  // ---------------------------
  // NAVIGATION & ZOOM HANDLERS
  // ---------------------------
  const handleGoBack = useCallback(() => navigate("/pdf"), [navigate]);
  const getFitWidthZoom = useCallback(() => {
    const c = containerRef.current;
    const b = baseSizeRef.current;
    return c && b ? c.clientWidth / b.w : 1;
  }, []);
  const getFitHeightZoom = useCallback(() => {
    const c = containerRef.current;
    const b = baseSizeRef.current;
    return c && b ? c.clientHeight / b.h : 1;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const fw = getFitWidthZoom();
      const fh = getFitHeightZoom();
      setFitWidthZoom(fw);
      if (fitMode === "width") setCanvasZoom(fw);
      if (fitMode === "height") setCanvasZoom(fh);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [fitMode, getFitWidthZoom, getFitHeightZoom]);

  const setCanvasZoom = (rawZoom) => {
    const container = containerRef.current;
    const baseSize = baseSizeRef.current;
    if (!container || !baseSize) return;
    const maxX = container.clientWidth / baseSize.w;
    setFitWidthZoom(maxX);
    const clamped = Math.min(Math.max(rawZoom, 0.5), maxX);
    setZoom(clamped);
    const f = frameRef.current;
    if (f) {
      f.style.width = `${baseSize.w * clamped}px`;
      f.style.height = `${baseSize.h * clamped}px`;
    }
  };
  const zoomIn = () => setCanvasZoom(Math.min(zoom + 0.1));
  const zoomOut = () => setCanvasZoom(Math.max(zoom - 0.1));
  const handleFitToggle = () =>
    setFitMode((m) => (m === "width" ? "height" : "width"));

  // PDF RENDERING
  const renderPage = useCallback(
    async (pageNumber) => {
      if (!pdfRef.current || !pdfCanvasRef.current) return;
      const page = await pdfRef.current.getPage(pageNumber);

      const viewport = page.getViewport({ scale: 1 });
      const cssW = viewport.width;
      const cssH = viewport.height;

      const { height: pdfH } = page.getViewport({ scale: 1 }).viewBox
        ? {
            width: page.getViewport({ scale: 1 }).viewBox[2],
            height: page.getViewport({ scale: 1 }).viewBox[3],
          }
        : { width: cssW, height: cssH };
      const ptPerPx = pdfH / cssH;
      baseSizeRef.current = { w: cssW, h: cssH, ptPerPx };
      const dpr = window.devicePixelRatio || 1;
      const canvas = pdfCanvasRef.current;
      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      renderTaskRef.current?.cancel();
      renderTaskRef.current = page.render({
        canvasContext: ctx,
        viewport,
      });

      try {
        await renderTaskRef.current.promise;
      } catch (err) {
        // PDF.js throws a "RenderingCancelledException" when you .cancel()
        // Just ignore that one—everything else log
        if (
          err?.name !== "RenderingCancelledException" &&
          err?.message !== "Rendering cancelled"
        ) {
          console.error("PDF render error:", err);
        }
      }
      // 1f) apply your fit-to-screen zoom
      const fitZoom =
        fitMode === "width" ? getFitWidthZoom() : getFitHeightZoom();
      setCanvasZoom(fitZoom);
    },
    [fitMode, getFitWidthZoom, getFitHeightZoom]
  );

  const generateThumbnails = async (pdfDocument) => {
    const thumbs = [];
    for (let i = 1; i <= pdfDocument.numPages; i++) {
      const page = await pdfDocument.getPage(i);
      const viewport = page.getViewport({ scale: 0.2 });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({
        canvasContext: canvas.getContext("2d"),
        viewport,
      }).promise;
      thumbs.push({ thumbnail: canvas.toDataURL() });
    }
    setPdfPages(thumbs);
  };

  // ---------------------------
  // DRAGGING FUNCTIONS (updated)
  // ---------------------------
  const startDrag = (e, id) => {
    // e.preventDefault();
    // e.stopPropagation();
    selectField(id);

    downXRef.current = e.clientX;
    downYRef.current = e.clientY;
    isDraggingRef.current = false;

    if (containerRef.current) {
      containerRef.current.style.touchAction = "none";
    }

    // Get the field data
    const field = fields.find((f) => f.id === id);
    if (!field) return;

    // Compute click position relative to the PDF canvas
    const rect = pdfCanvasRef.current.getBoundingClientRect();
    const clickX = (e.clientX - rect.left) / zoom;
    const clickY = (e.clientY - rect.top) / zoom;

    // Record how far into the box we clicked
    startXRef.current = clickX - field.baseLeft;
    startYRef.current = clickY - field.baseTop;

    dragIndexRef.current = id;

    // Listen for pointer moves & up on the whole window
    document.addEventListener("pointermove", drag);
    document.addEventListener("pointerup", stopDrag);
  };

  const drag = (e) => {
    e.preventDefault(); // prevent page/container from auto-scrolling
    if (dragIndexRef.current == null) return;

    if (!isDraggingRef.current) {
      const dx = e.clientX - downXRef.current;
      const dy = e.clientY - downYRef.current;
      if (Math.hypot(dx, dy) < 5) return;
      // now initiate drag
      isDraggingRef.current = true;
      dragIndexRef.current = selectedFieldId;
    }

    const container = containerRef.current;
    if (container) {
      const { top, bottom, left, right } = container.getBoundingClientRect();
      if (e.clientY < top + SCROLL_EDGE) container.scrollTop -= SCROLL_SPEED;
      else if (e.clientY > bottom - SCROLL_EDGE)
        container.scrollTop += SCROLL_SPEED;
      if (e.clientX < left + SCROLL_EDGE) container.scrollLeft -= SCROLL_SPEED;
      else if (e.clientX > right - SCROLL_EDGE)
        container.scrollLeft += SCROLL_SPEED;
    }

    const field = fields.find((f) => f.id === dragIndexRef.current);
    if (!field) return;

    const rect = pdfCanvasRef.current.getBoundingClientRect();
    const clickX = (e.clientX - rect.left) / zoom;
    const clickY = (e.clientY - rect.top) / zoom;

    // Snap to grid:
    let snappedLeft =
      Math.round((clickX - startXRef.current) / GRID_SIZE) * GRID_SIZE;
    let snappedTop =
      Math.round((clickY - startYRef.current) / GRID_SIZE) * GRID_SIZE;

    const canvasW = baseSizeRef.current.w;
    const canvasH = baseSizeRef.current.h;
    const fieldW = field.width ?? field.baseWidth;
    const fieldH = field.height ?? field.baseHeight;
    snappedLeft = Math.min(Math.max(0, snappedLeft), canvasW - fieldW);
    snappedTop = Math.min(Math.max(0, snappedTop), canvasH - fieldH);

    updateField(field.id, {
      baseLeft: snappedLeft,
      baseTop: snappedTop,
    });
  };

  const stopDrag = () => {
    dragIndexRef.current = null;
    isDraggingRef.current = false;

    // restore native scrolling
    if (containerRef.current) {
      containerRef.current.style.overflow = "";
    }

    document.removeEventListener("pointermove", drag);
    document.removeEventListener("pointerup", stopDrag);
  };

  useEffect(() => {
    const handleArrowKeys = (e) => {
      if (!selectedFieldId || isEditingText) return;
      const step = e.shiftKey ? GRID_SIZE : 1;
      const field = fields.find((f) => f.id === selectedFieldId);
      if (!field) return;

      let { baseLeft, baseTop } = field;
      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          baseTop -= step;
          break;
        case "ArrowDown":
          e.preventDefault();
          baseTop += step;
          break;
        case "ArrowLeft":
          e.preventDefault();
          baseLeft -= step;
          break;
        case "ArrowRight":
          e.preventDefault();
          baseLeft += step;
          break;
        default:
          return;
      }

      updateField(selectedFieldId, { baseLeft, baseTop });
    };

    window.addEventListener("keydown", handleArrowKeys);
    return () => window.removeEventListener("keydown", handleArrowKeys);
  }, [selectedFieldId, fields, updateField, isEditingText]);

  // ---------------------------
  // RESIZING FUNCTIONS
  // ---------------------------
  const startResize = (e, id, side = "right") => {
    e.stopPropagation();
    const field = fields.find((f) => f.id === id);
    if (!field) return;
    resizeFieldRef.current = id;
    resizeSideRef.current = side;
    resizeStartLeftRef.current = field.baseLeft;

    resizeStartXRef.current = e.clientX;
    resizeStartYRef.current = e.clientY;
    resizeStartWidthRef.current = field.width ?? field.baseWidth;
    resizeStartHeightRef.current = field.height ?? field.baseHeight;
    resizeStartTopRef.current = field.baseTop;
    document.addEventListener("mousemove", onResize);
    document.addEventListener("mouseup", stopResize);
  };

  const onResize = (e) => {
    if (!resizeFieldRef.current) return;
    const { ptPerPx } = baseSizeRef.current;

    setFields((prevFields) =>
      prevFields.map((field) => {
        if (field.id !== resizeFieldRef.current) return field;

        const side = resizeSideRef.current;

        const deltaX = ((e.clientX - resizeStartXRef.current) / zoom) * ptPerPx;
        const deltaY = ((e.clientY - resizeStartYRef.current) / zoom) * ptPerPx;

        // === IMAGE: make it square, based on the larger delta ===
        if (field.type === "image") {
          let delta,
            newLeft = resizeStartLeftRef.current,
            newTop = resizeStartTopRef.current;

          switch (side) {
            case "se":
              delta = Math.max(deltaX, deltaY);
              break;
            case "sw":
              delta = Math.max(-deltaX, deltaY);
              newLeft = resizeStartLeftRef.current - delta;
              break;
            case "ne":
              delta = Math.max(deltaX, -deltaY);
              newTop = resizeStartTopRef.current - delta;
              break;
            case "nw":
              delta = Math.max(-deltaX, -deltaY);
              newLeft = resizeStartLeftRef.current - delta;
              newTop = resizeStartTopRef.current - delta;
              break;
            default:
              delta = 0;
          }

          const newSize = Math.max(20, resizeStartWidthRef.current + delta);
          return {
            ...field,
            baseWidth: newSize,
            baseHeight: newSize,
            baseLeft: newLeft,
            baseTop: newTop,
          };
        }

        // === TEXTBOX: horizontal resize only ===
        if (field.type === "textbox") {
          if (side === "right") {
            return {
              ...field,
              width: Math.max(20, resizeStartWidthRef.current + deltaX),
            };
          } else {
            const newWidth = Math.max(20, resizeStartWidthRef.current - deltaX);
            const newLeft = resizeStartLeftRef.current + deltaX;
            return {
              ...field,
              width: newWidth,
              baseLeft: newLeft,
            };
          }
        }

        // === OTHERS: resize width and height ===
        return {
          ...field,
          width: Math.max(20, resizeStartWidthRef.current + deltaX),
          height: Math.max(20, resizeStartHeightRef.current + deltaY),
        };
      })
    );
  };

  const stopResize = () => {
    resizeFieldRef.current = null;
    document.removeEventListener("mousemove", onResize);
    document.removeEventListener("mouseup", stopResize);
  };

  const centerCanvas = () => {
    const container = containerRef.current;
    const canvas = pdfCanvasRef.current;
    if (!container || !canvas) return;
    container.scrollLeft = (canvas.width - container.clientWidth) / 2;
    // container.scrollTop = (canvas.height - container.clientHeight) / 2;
  };

  const handleDownload = useCallback(async () => {
    if (!pdfBytes) {
      return toast.error("PDF not loaded yet");
    }

    try {
      const filledBytes = await generateFilledPdf(pdfBytes, fields);

      const blob = new Blob([filledBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pdf?.name || "filled"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF");
    }
  }, [pdfBytes, fields, pdf?.name]);

  // ---------------------------
  // EFFECTS
  // ---------------------------

  // Set fields only if they have changed.
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  useEffect(() => {
    if (pdf && pdf.fields) {
      const loadedFields = Array.isArray(pdf.fields)
        ? pdf.fields
        : Object.values(pdf.fields).flat();

      setFields((prevFields) => {
        const prevJSON = JSON.stringify(prevFields);
        const newJSON = JSON.stringify(loadedFields);
        return prevJSON !== newJSON ? loadedFields : prevFields;
      });

      // Reset counters based on the ACTUAL number of existing fields
      const textboxCounts = {};
      const checkboxCounts = {};

      for (const field of loadedFields) {
        if (!field.page) continue;

        if (field.type === "textbox") {
          textboxCounts[field.page] = (textboxCounts[field.page] || 0) + 1;
        }

        if (field.type === "checkbox") {
          checkboxCounts[field.page] = (checkboxCounts[field.page] || 0) + 1;
        }
      }

      textboxCountersRef.current = textboxCounts;
      checkboxCountersRef.current = checkboxCounts;
    }
  }, [pdf]);

  useEffect(() => {
    if (pdf?.fileId && userData && !pdfRef.current) {
      renderPdf();
    }
  }, [pdf?.fileId, userData, renderPdf]);

  // Render page on change
  useEffect(() => {
    if (pdfReady && pdfRef.current && pdfCanvasRef.current) {
      renderPage(currentPage);
    }
  }, [pdfReady, currentPage, renderPage]);

  // Select PDF from context
  useEffect(() => {
    if (id) {
      const found = pdfs.find((p) => p.id === id) || null;
      setPdf((prev) => (prev?.id !== found?.id ? found : prev));
      setPdfReady(false);
    }
  }, [id, pdfs]);

  useEffect(() => {
    return () => renderTaskRef.current?.cancel();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!isPanMode || !container) return;

    let originX, originY, startScrollLeft, startScrollTop;

    const onPointerDown = (e) => {
      e.preventDefault();
      originX = e.clientX;
      originY = e.clientY;
      startScrollLeft = container.scrollLeft;
      startScrollTop = container.scrollTop;
      container.style.cursor = "grabbing";
      container.setPointerCapture(e.pointerId);
      container.addEventListener("pointermove", onPointerMove);
      container.addEventListener("pointerup", onPointerUp);
    };

    const scrollSpeed = 0.1;

    const onPointerMove = (e) => {
      const dx = e.clientX - originX;
      const dy = e.clientY - originY;

      container.scrollLeft = startScrollLeft - dx * scrollSpeed;
      container.scrollTop = startScrollTop - dy * scrollSpeed;
    };

    const onPointerUp = (e) => {
      container.style.cursor = "grab";
      container.releasePointerCapture(e.pointerId);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
    };

    container.style.touchAction = "none";
    container.style.cursor = "grab";
    container.addEventListener("pointerdown", onPointerDown);

    return () => {
      container.style.cursor = "";
      container.style.touchAction = "";
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
    };
  }, [isPanMode]);

  useLayoutEffect(() => {
    if (!pdfLoading) {
      centerCanvas();
    }
  }, [pdfLoading, currentPage]);

  // Filter the fields by the current page.
  const fieldsOnCurrentPage = fields.filter((f) => f.page === currentPage);

  if (!googleDriveId || pdfLoading) {
    return <Loader text="Loading PDF…" />;
  }
  return (
    <>
      <div className="sticky top-0 z-20 bg-custom border-alt-color">
        <PdfToolbar
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          onSignatureClick={onSignatureClick}
          handleImageUpload={handleImageUpload}
          saveFieldPositions={saveFieldPositions}
          handleDownload={handleDownload}
          pdf={pdf}
          onToggleLeftSidebar={() => setShowLeftSidebar((v) => !v)}
          onToggleRightSidebar={() => setShowRightSidebar((v) => !v)}
          showLeftSidebar={showLeftSidebar}
          showRightSidebar={showRightSidebar}
        />
      </div>
      <div
        className={`relative flex flex-row w-full h-[calc(100vh-48px)] overflow-hidden`}
      >
        {showSigner && (
          <AutoSignature
            visible={showSigner}
            fieldId={selectedFieldId}
            initialDataUrl={sigUrl}
            onSave={(fieldId, dataUrl) => handleSignatureSave(fieldId, dataUrl)}
            onCancel={() => {
              setActiveTool(null);
              setShowSigner(false);
            }}
          />
        )}

        {/* Left Sidebar: Thumbnails */}
        <aside
          className={`
        ${showLeftSidebar ? "block" : "hidden"}
        absolute top-0 left-0 bottom-0 w-48
        bg-slate-50/90 text-tier-primary backdrop-blur shadow-lg z-30
        lg:text-color lg:static lg:flex lg:flex-col lg:h-full lg:min-h-0 lg:w-48 lg:bg-white/30 lg:backdrop-none lg:shadow-none
      `}
        >
          <div className="sticky top-0 z-10 p-2 backdrop-blur">
            <h2 className="text-base text-center font-semibold">Pages</h2>
          </div>
          <div
            className={`p-4 space-y-2 scrollable overflow-y-auto lg:h-full h-[calc(100vh-80px)] min-h-0`}
          >
            {pdfPages.map((page, i) => (
              <div
                key={i}
                onClick={() => handleThumbnailClick(i + 1)}
                className={`cursor-pointer border p-1 rounded ${
                  currentPage === i + 1
                    ? "border-tier-secondary"
                    : "border-alt-color"
                }`}
              >
                <img
                  src={page.thumbnail}
                  alt={`Page ${i + 1}`}
                  className="w-full"
                />
                <div className="text-center text-xs mt-1">Page {i + 1}</div>
              </div>
            ))}
          </div>
        </aside>

        {/* Main Viewer */}
        <div className="flex flex-col flex-1 w-full overflow-x-auto overflow-y-hidden">
          <div className="sticky top-0 z-10 h-[32px] transition-all duration-300 ease-in-out">
            <TextEditorToolbar
              selectedField={fields.find((f) => f.id === selectedFieldId)}
              updateField={updateField}
              selectedFontSize={selectedFontSize}
              setSelectedFontSize={setSelectedFontSize}
              selectedFontFamily={selectedFontFamily}
              setSelectedFontFamily={setSelectedFontFamily}
            />
            <CheckboxEditorToolbar
              selectedField={fields.find((f) => f.id === selectedFieldId)}
              updateField={updateField}
              selectedCheckbox={selectedCheckbox}
              setSelectedCheckbox={setSelectedCheckbox}
            />
          </div>
          <div className="flex-1 flex justify-center relative p-4 overflow-hidden">
            <div
              ref={containerRef}
              className={`pdf-container overflow-auto w-full flex items-start justify-center
               ${isPanMode ? "cursor-grab" : ""}
               ${isTextboxMode ? "cursor-text" : ""}
               ${isCheckboxMode ? "cursor-crosshair" : ""}
             `}
            >
              <div
                ref={frameRef}
                onClick={handleCanvasClick}
                className="relative"
                style={{
                  width: baseSizeRef.current
                    ? `${baseSizeRef.current.w * zoom}px`
                    : undefined,
                  height: baseSizeRef.current
                    ? `${baseSizeRef.current.h * zoom}px`
                    : undefined,
                }}
              >
                <canvas
                  ref={pdfCanvasRef}
                  style={{ width: "100%", height: "100%" }}
                  className="block border border-gray-300"
                />

                {fieldsOnCurrentPage.map((field) => {
                  switch (field.type) {
                    case "checkbox":
                      return (
                        <AutoCheckbox
                          key={field.id}
                          field={field}
                          updateField={updateField}
                          selectField={selectField}
                          onContainerMouseDown={startDrag}
                          onContainerMouseUp={stopDrag}
                          onStartResize={startResize}
                          isSelected={selectedFieldId === field.id}
                          zoom={zoom}
                        />
                      );
                    case "textbox":
                      return (
                        <AutoResizeTextBox
                          key={field.id}
                          field={field}
                          updateField={updateField}
                          onStartResize={startResize}
                          onContainerMouseDown={startDrag}
                          onContainerMouseUp={stopDrag}
                          selectField={selectField}
                          isSelected={selectedFieldId === field.id}
                          zoom={zoom}
                          onEditStart={() => setIsEditingText(true)}
                          onEditEnd={() => setIsEditingText(false)}
                          isEditingText={isEditingText}
                          setIsEditingText={setIsEditingText}
                        />
                      );
                    case "image":
                      return (
                        <AutoImage
                          key={field.id}
                          field={field}
                          updateField={updateField}
                          isSelected={selectedFieldId === field.id}
                          zoom={zoom}
                          selectField={selectField}
                          startDrag={startDrag}
                          stopDrag={stopDrag}
                        />
                      );
                    case "signature":
                      return (
                        <AutoImage
                          key={field.id}
                          field={{ ...field, src: sigUrl || field.src }}
                          updateField={updateField}
                          isSelected={selectedFieldId === field.id}
                          zoom={zoom}
                          selectField={selectField}
                          startDrag={startDrag}
                          stopDrag={stopDrag}
                          onDoubleClick={() => handleDoubleClick(field)}
                        />
                      );
                    default:
                      return null;
                  }
                })}
              </div>
            </div>
            {saveLoading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-50">
                <Loader text="Saving File…" />
              </div>
            )}

            <BottomControls
              handleGoBack={handleGoBack}
              previousPage={previousPage}
              nextPage={nextPage}
              currentPage={currentPage}
              totalPages={totalPages}
              fitWidthZoom={fitWidthZoom}
              zoom={zoom}
              zoomIn={zoomIn}
              zoomOut={zoomOut}
              fitMode={fitMode}
              toggleFit={handleFitToggle}
            />
          </div>
        </div>

        {/* Right Sidebar: Field Manager */}
        <aside
          className={`
        ${showRightSidebar ? "block" : "hidden"}
        absolute top-0 right-0 bottom-0 w-80
        bg-slate-50/90 backdrop-blur shadow-lg z-40
        lg:static lg:flex lg:flex-col lg:w-80 lg:h-full lg:min-h-0 lg:bg-white/40 lg:backdrop-none lg:shadow-none
      `}
        >
          <div className="sticky top-0 z-10 p-2 backdrop-blur">
            <h2 className="text-base text-center font-semibold text-tier-primary">
              Field Manager
            </h2>
          </div>
          <div
            className={`flex-1 overflow-y-auto h-[calc(100vh-80px)] min-h-0 scrollable`}
          >
            <FieldManagerPanel
              fields={fields}
              selectedFieldId={selectedFieldId}
              selectField={selectField}
              removeField={removeField}
              updateFieldName={updateFieldName}
              scrollFieldIntoView={scrollFieldIntoView}
            />
          </div>
        </aside>
      </div>
    </>
  );
};

export default PdfWithSidebar;
